import React, {useState} from 'react';

import {AnalyticsMetadataForContext, AnalyticsModalType} from 'analytics/AnalyticsTypes';
import {SampleRestrictionStatus} from 'constants/SampleStatusConstants';
import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {
  canCurrentUserSetSpecifiedRestrictionStatus,
  getEditSampleRestrictionStatusDisabledMessage,
  getRestrictionStatusStringFromEnum,
  getValidRestrictionStatusesForContainer,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import notifications from 'notifications';
import {ContainerPropertiesUpdate} from 'samples/ContainerDAO';
import {bulkUpdateContainerPropertiesWithDataMods} from 'samples/StorableActions';
import BaseBulkEditSamplesModal from 'samples/toolbar/BaseBulkEditSamplesModal';
import {
  BaseBulkUpdateSampleRolesModalProps,
  ItemWithSampleRolesAndPermissions,
} from 'samples/toolbar/BulkUpdateSampleRolesModal';
import CurrentAndNewValueRow from 'samples/toolbar/CurrentAndNewValueRow';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';

export interface BulkUpdateRestrictionStatusModalProps extends BaseBulkUpdateSampleRolesModalProps {
  items: Array<ItemWithSampleRolesAndPermissions>;
  analyticsMetadata: AnalyticsMetadataForContext;
}

export const MULTIPLE_VALUES_STRING = 'Multiple values';

function getCurrentValueText(items: Array<ItemWithSampleRolesAndPermissions>): string {
  const dedupedCurrentValues = new Set(items.map((item) => item.restrictionStatus));
  if (dedupedCurrentValues.size === 1) {
    return getRestrictionStatusStringFromEnum(Array.from(dedupedCurrentValues)[0]);
  } else {
    return MULTIPLE_VALUES_STRING;
  }
}

export default function BulkUpdateRestrictionStatusModal(
  props: BulkUpdateRestrictionStatusModalProps
): JSX.Element {
  const [restrictionStatus, setRestrictionStatus] = useState<SampleRestrictionStatus | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const analyticsMetadataDict = {
    ...props.analyticsMetadata,
    modalType: AnalyticsModalType.BULK_UPDATE_RESTRICTION_STATUS,
  };

  const handleChangeStatus = (newStatus: SampleRestrictionStatus): void => {
    if (newStatus === SampleRestrictionStatus.UNRESTRICTED) {
      setNotice(
        `By unrestricting these items, you will allow anyone to use them and remove all ${getRestrictedSampleUsersDisplayText()} as well.`
      );
    } else {
      setNotice(null);
    }
    setRestrictionStatus(newStatus);
  };

  const handleRequestSubmit = (restrictionStatus: SampleRestrictionStatus): void => {
    for (const item of props.items) {
      if (!canCurrentUserSetSpecifiedRestrictionStatus(item, restrictionStatus)) {
        notifications.error(getEditSampleRestrictionStatusDisabledMessage(item.restrictionStatus));
        return;
      }
    }
    const containerUpdates: Array<ContainerPropertiesUpdate> = props.items.map((item) => ({
      id: item.id,
      restrictionStatus,
    }));
    bulkUpdateContainerPropertiesWithDataMods(containerUpdates, analyticsMetadataDict);
    props.onRequestClose();
  };

  const options = getValidRestrictionStatusesForContainer().map((status) => ({
    label: getRestrictionStatusStringFromEnum(status),
    value: status,
  }));

  return (
    <BaseBulkEditSamplesModal
      modalTitle="Bulk update restriction status"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        handleRequestSubmit(restrictionStatus!);
      }}
      itemIds={props.items.map(({id}) => id)}
      analyticsMetadata={analyticsMetadataDict}
      modalContent={
        <>
          <CurrentAndNewValueRow
            label="restriction status"
            currentValue={getCurrentValueText(props.items)}
            newValueInput={
              <RadioButtonSelector
                dataTestAttrs={{component: 'BulkUpdateRestrictionStatusModal', element: 'radioButtonGroup'}}
                options={options}
                onChange={handleChangeStatus}
                isVertical={true}
                selectedValue={restrictionStatus}
              />
            }
          />
        </>
      }
      infoNoticeProps={notice != null ? {text: notice} : undefined}
      isSubmitDisabled={() => restrictionStatus == null}
    />
  );
}
